import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, FileText, CheckCircle, XCircle, 
  TrendingUp, Clock, ChevronRight, Sparkles,
  LogOut, User, FolderOpen, Download, File, HardDrive
} from 'lucide-react';
import { clientAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [dataRoomFiles, setDataRoomFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('assessments'); // 'assessments' or 'dataroom'

  useEffect(() => {
    fetchAssessments();
    fetchDataRoomFiles();
  }, []);

  const fetchAssessments = async () => {
    try {
      const response = await clientAPI.getAssessments();
      setAssessments(response.data);
      if (response.data.length > 0) {
        handleSelectAssessment(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataRoomFiles = async () => {
    try {
      const response = await clientAPI.getDataRoomFiles();
      setDataRoomFiles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch data room files:', error);
    }
  };

  const handleSelectAssessment = async (assessment) => {
    setSelectedAssessment(assessment);
    setDetailsLoading(true);
    try {
      const response = await clientAPI.getAssessmentDetails(assessment.id);
      setAssessmentDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch assessment details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getTypeConfig = (type) => {
    const configs = {
      'CMMC': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      'HIPAA': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      'NIST_CSF': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' }
    };
    return configs[type] || configs['HIPAA'];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading your assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur-lg sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-violet-200 font-semibold">Aspiritech</span>
                <span className="text-slate-400 text-sm block">Client Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-400">
                <User className="w-4 h-4" />
                <span className="text-sm">{user?.firstName} {user?.lastName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Your Assessment Results</h1>
          <p className="text-slate-400">View your assigned cybersecurity assessment results and compliance status.</p>
        </div>

        {assessments.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Assessments Assigned</h3>
            <p className="text-slate-400">
              You don&apos;t have any assessments assigned yet. Please contact your administrator.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Assessment List */}
            <div className="lg:col-span-1">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-700">
                  <h2 className="font-semibold text-white">Assessments ({assessments.length})</h2>
                </div>
                <div className="divide-y divide-slate-700">
                  {assessments.map((assessment) => {
                    const typeConfig = getTypeConfig(assessment.assessmentType);
                    const isSelected = selectedAssessment?.id === assessment.id;
                    const displayType = assessment.assessmentType === 'NIST_CSF' ? 'NIST CSF' : assessment.assessmentType;
                    
                    return (
                      <button
                        key={assessment.id}
                        onClick={() => handleSelectAssessment(assessment)}
                        className={`w-full p-4 text-left transition-colors ${
                          isSelected 
                            ? 'bg-violet-500/10 border-l-2 border-violet-500' 
                            : 'hover:bg-slate-700/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-white text-sm mb-1">{assessment.name}</h3>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${typeConfig.bg} ${typeConfig.text}`}>
                                {displayType}
                              </span>
                              <span className="text-xs text-slate-500">{formatDate(assessment.createdAt)}</span>
                            </div>
                          </div>
                          <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-violet-400' : 'text-slate-500'}`} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Assessment Details */}
            <div className="lg:col-span-2">
              {detailsLoading ? (
                <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : assessmentDetails ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-white mb-2">
                          {assessmentDetails.assessment.name}
                        </h2>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Last updated: {formatDate(assessmentDetails.assessment.lastModified)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-4xl font-bold text-white mb-1">
                          {assessmentDetails.summary.complianceScore}%
                        </div>
                        <div className="text-sm text-slate-400">Compliance Score</div>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-400 text-xs">Total</span>
                      </div>
                      <div className="text-xl font-bold text-white">{assessmentDetails.summary.totalQuestions}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-slate-400 text-xs">Compliant</span>
                      </div>
                      <div className="text-xl font-bold text-green-400">{assessmentDetails.summary.compliant}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <div className="flex items-center gap-2 mb-1">
                        <XCircle className="w-4 h-4 text-red-400" />
                        <span className="text-slate-400 text-xs">Non-Compliant</span>
                      </div>
                      <div className="text-xl font-bold text-red-400">{assessmentDetails.summary.nonCompliant}</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-blue-400" />
                        <span className="text-slate-400 text-xs">Answered</span>
                      </div>
                      <div className="text-xl font-bold text-blue-400">{assessmentDetails.summary.answered}</div>
                    </div>
                  </div>

                  {/* Category Breakdown */}
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-700">
                      <h3 className="font-semibold text-white">Category Breakdown</h3>
                    </div>
                    <div className="p-6 space-y-4">
                      {assessmentDetails.categoryBreakdown.map((category, index) => {
                        const percentage = category.complianceScore;
                        return (
                          <div key={index}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-white text-sm">{category.category}</span>
                              <span className={`text-sm font-semibold ${
                                percentage >= 80 ? 'text-green-400' :
                                percentage >= 50 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {percentage}%
                              </span>
                            </div>
                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  percentage >= 80 ? 'bg-green-500' :
                                  percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Top Findings */}
                  {assessmentDetails.deficiencies.length > 0 && (
                    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                      <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                        <h3 className="font-semibold text-white">
                          Key Findings ({assessmentDetails.deficiencies.length})
                        </h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">
                          Requires Attention
                        </span>
                      </div>
                      <div className="divide-y divide-slate-700 max-h-96 overflow-y-auto">
                        {assessmentDetails.deficiencies.slice(0, 10).map((item, index) => (
                          <div key={index} className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-red-500/20 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                                <XCircle className="w-3 h-3 text-red-400" />
                              </div>
                              <div>
                                <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300 mb-1 inline-block">
                                  {item.category}
                                </span>
                                <p className="text-sm text-white">{item.question}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700 mt-auto">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Shield className="w-4 h-4" />
            <span>Powered by Aspiritech Cybersecurity Assessment Tool</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientDashboard;
